import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { FileDown, Loader2, CheckCircle, AlertCircle } from "lucide-react";

export type PdfExportMethod = 'traditional' | 'fullpage';

interface ExportPdfDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (language: 'en' | 'pt', method: PdfExportMethod) => Promise<void>;
  companyName: string;
  productName: string;
  year: number;
  isLoading?: boolean;
  error?: string | null;
}

export const ExportPdfDialog = ({
  isOpen,
  onClose,
  onExport,
  companyName,
  productName,
  year,
  isLoading = false,
  error = null,
}: ExportPdfDialogProps) => {
  const { t } = useLanguage();
  const [selectedLanguage, setSelectedLanguage] = useState<'en' | 'pt'>('pt');
  // Always use fullpage method - traditional method is commented out for now
  const exportMethod: PdfExportMethod = 'fullpage';
  const [isExporting, setIsExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    setExportSuccess(false);
    try {
      await onExport(selectedLanguage, exportMethod);
      setExportSuccess(true);
      // Auto-close after success
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (error) {
      console.error('Error exporting PDF:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleClose = () => {
    if (!isExporting && !isLoading) {
      onClose();
      // Reset state when closing
      setSelectedLanguage('pt');
      setExportSuccess(false);
    }
  };

  const isProcessing = isLoading || isExporting;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileDown className="h-5 w-5" />
            {t("results.sharing.dialog.title")}
          </DialogTitle>
          <DialogDescription>
            {t("results.sharing.dialog.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Preview info */}
          <div className="rounded-lg bg-gray-50 p-3 text-sm">
            <p className="font-medium text-gray-700">{companyName}</p>
            <p className="text-gray-600">
              {productName} ({year})
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Success message */}
          {exportSuccess && (
            <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <p className="text-green-700">{t("results.sharing.success.pdfExported")}</p>
            </div>
          )}

          {/*
            Export method selection removed - Only fullpage method is currently available.
            Traditional method is commented out and may be re-enabled in the future.
            To re-enable: uncomment the state variable and UI below, then add translations.
          */}

          {/* Language selection */}
          <div className="space-y-2">
            <Label htmlFor="language">
              {t("results.sharing.dialog.languageLabel")}
            </Label>
            <Select
              value={selectedLanguage}
              onValueChange={(value) => setSelectedLanguage(value as 'en' | 'pt')}
              disabled={isProcessing}
            >
              <SelectTrigger id="language">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pt">{t("results.sharing.dialog.portuguese")}</SelectItem>
                <SelectItem value="en">{t("results.sharing.dialog.english")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="flex gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isProcessing}
          >
            {t("cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleExport}
            disabled={isProcessing || exportSuccess}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isExporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("results.sharing.dialog.exporting")}
              </>
            ) : exportSuccess ? (
              <>
                <CheckCircle className="mr-2 h-4 w-4" />
                {t("success")}
              </>
            ) : (
              <>
                <FileDown className="mr-2 h-4 w-4" />
                {t("results.sharing.dialog.exportButton")}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
